import { useQuery } from "@tanstack/react-query";

import type { DocumentWorkflowFileVersion } from "../entities/document-workflow";
import { useDocumentFiles } from "./documentFilesContext";
import { SourceBadge } from "./FileBadges";
import { displayDateTime } from "./fileLabels";

type Provenance = DocumentWorkflowFileVersion["provenance"];

/** Строковое поле обратной ссылки: значения контракта — свободный объект. */
function referenceValue(
  reference: Provenance["reference"],
  key: string,
): string | null {
  const value = reference?.[key];

  return typeof value === "string" && value !== "" ? value : null;
}

export function EmailSource({ messageId }: { messageId: string }) {
  const { services, onShowInMail } = useDocumentFiles();
  const message = useQuery({
    queryKey: ["document-workflow", "mail-messages", messageId],
    queryFn: () => services.documentWorkflow.message(messageId),
    retry: false,
  });
  const from = message.data?.from;
  const when = message.data?.received_at ?? message.data?.sent_at ?? null;

  return (
    <>
      {message.data ? (
        <span>
          Письмо
          {from
            ? ` от ${from.name ? `${from.name} <${from.email}>` : from.email}`
            : ""}
          {when ? ` · ${displayDateTime(when)}` : ""}
          {message.data.subject ? ` · «${message.data.subject}»` : ""}
        </span>
      ) : message.isPending ? (
        <span aria-busy="true">Загружаем сведения о письме…</span>
      ) : (
        <span>Сведения о письме недоступны.</span>
      )}
      {onShowInMail ? (
        <button
          className="button button--text button--compact"
          type="button"
          onClick={() => onShowInMail(messageId)}
        >
          Показать в переписке
        </button>
      ) : null}
    </>
  );
}

export function CommentSource({ commentId }: { commentId: string }) {
  const {
    services,
    documentId,
    canViewDiscussion,
    authorName,
    onShowInDiscussion,
  } = useDocumentFiles();
  // Тот же ключ, что у ленты обсуждения в карточке: запрос делится с ней.
  const comments = useQuery({
    queryKey: ["document-workflow", "documents", documentId, "comments"],
    queryFn: () => services.documentWorkflow.comments(documentId),
    enabled: canViewDiscussion,
    retry: false,
  });
  const comment = comments.data?.find((item) => item.id === commentId);

  return (
    <>
      {comment ? (
        <span>
          Сообщение обсуждения
          {` · ${comment.author?.display_name ?? authorName(comment.author_id)}`}
          {` · ${displayDateTime(comment.created_at)}`}
        </span>
      ) : (
        <span>Сообщение внутреннего обсуждения.</span>
      )}
      {onShowInDiscussion && canViewDiscussion ? (
        <button
          className="button button--text button--compact"
          type="button"
          onClick={() => onShowInDiscussion(commentId)}
        >
          Показать в обсуждении
        </button>
      ) : null}
    </>
  );
}

/**
 * Происхождение файла: откуда он взялся и как к источнику вернуться.
 *
 * Обратная ссылка приходит только на источник, разрешённый пользователю, —
 * иначе сервер отдаёт `null`, и тогда виден лишь тип источника. Один объект
 * хранилища может стоять сразу в нескольких связях: письме, документе,
 * обсуждении, материале, — поэтому счётчик связей показывается отдельно.
 */
export function FileProvenance({
  provenance,
  referenceCount,
  authorName,
}: {
  provenance: Provenance;
  referenceCount?: number;
  authorName: (id: string | null) => string;
}) {
  const messageId = referenceValue(provenance.reference, "mail_message_id");
  const commentId = referenceValue(provenance.reference, "comment_id");

  return (
    <div className="file-provenance">
      <SourceBadge type={provenance.type} />
      {provenance.type === "email_attachment" && messageId ? (
        <EmailSource messageId={messageId} />
      ) : null}
      {provenance.type === "comment_attachment" && commentId ? (
        <CommentSource commentId={commentId} />
      ) : null}
      {(provenance.type === "email_attachment" && !messageId) ||
      (provenance.type === "comment_attachment" && !commentId) ? (
        <span className="dw-secondary">
          Ссылка на источник недоступна: он вне ваших прав или другого контура.
        </span>
      ) : null}
      {provenance.linked_at ? (
        <span className="dw-secondary">
          Привязан {displayDateTime(provenance.linked_at)}
          {provenance.linked_by ? ` · ${authorName(provenance.linked_by)}` : ""}
        </span>
      ) : null}
      {referenceCount !== undefined && referenceCount > 1 ? (
        <span className="dw-secondary">
          Этот файл используется в связях: {referenceCount}. Байты хранятся в
          одном экземпляре.
        </span>
      ) : null}
    </div>
  );
}
